import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileUp, Loader2, Upload } from 'lucide-react';

type Probleme = { ligne: number; type: string; message: string };
type FeuilleRapport = {
  feuille: string;
  lignes: number;
  importables: number;
  importes: number;
  problemes: Probleme[];
  notes: string[];
};
type Rapport = {
  success: boolean;
  appliquee: boolean;
  message: string;
  feuilles?: FeuilleRapport[];
  importables?: number;
  importes?: number;
};

/**
 * L'import du classeur de reprise — le pendant du bouton d'export.
 *
 * Réservé au super administrateur : la reprise est promise « gratuite sous
 * réserve d'un tableau bien rempli », et c'est un humain qui juge le tableau.
 * Un bouton chez le client transformerait toute reprise ratée en ticket.
 *
 * Toujours en deux temps. Le premier envoi analyse et ne touche à rien : il
 * dit, feuille par feuille, ce qui entrera, ce qui existe déjà, ce qui ne se
 * lit pas — ligne par ligne. Le second envoi, du même fichier, importe. Un
 * import qu'on ne peut pas relire avant de le lancer est un import qu'on
 * lance les yeux fermés.
 */
export default function ImporterDesDonnees() {
  const queryClient = useQueryClient();
  const fichierRef = useRef<HTMLInputElement>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [rapport, setRapport] = useState<Rapport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const envoyer = async (appliquer: boolean) => {
    if (!fichier) return;
    setErreur(null);
    setEnCours(true);
    try {
      const corps = new FormData();
      corps.append('classeur', fichier);
      corps.append('appliquer', appliquer ? '1' : '0');

      const reponse = await fetch('/api/admin/import-reprise', {
        method: 'POST',
        credentials: 'include',
        body: corps,
      });
      const donnees = await reponse.json();
      if (!reponse.ok || !donnees.success) {
        throw new Error(donnees.message ?? 'L\'import a échoué.');
      }
      setRapport(donnees);

      if (donnees.appliquee) {
        // Tout ce que l'import a pu toucher devient périmé d'un coup.
        ['adherents', 'bureau-sponsors', 'evenements', 'bureau-exercice-archives', 'bureau-exercice-chiffres']
          .forEach((cle) => queryClient.invalidateQueries({ queryKey: [cle] }));
      }
    } catch (e: any) {
      setErreur(e?.message ?? 'L\'import a échoué.');
      setRapport(null);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Le classeur de reprise du shop, rempli par l'association, s'importe ici — en deux temps :
        l'analyse d'abord, qui ne touche à rien et dit ligne par ligne ce qui entrera ; l'import
        ensuite, du même fichier. Les doublons sont écartés, les lignes d'exemple reconnues, et ce
        que la reprise ne couvre pas est dit plutôt que passé sous silence.{' '}
        <a
          href="https://shop.gest-asso.com/api/modele-import"
          target="_blank"
          rel="noopener"
          className="underline font-medium text-primary-700 dark:text-primary-300"
        >
          Télécharger le classeur vierge
        </a>{' '}
        — le même fichier que celui proposé sur le bilan de l'association.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fichierRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={(e) => {
            setFichier(e.target.files?.[0] ?? null);
            setRapport(null);
            setErreur(null);
          }}
        />
        <button
          type="button"
          onClick={() => fichierRef.current?.click()}
          className="btn-outline inline-flex items-center gap-2"
        >
          <FileUp className="w-4 h-4" />
          {fichier ? fichier.name : 'Choisir le classeur (.xlsx)'}
        </button>

        <button
          type="button"
          onClick={() => void envoyer(false)}
          disabled={!fichier || enCours}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {enCours && !rapport?.appliquee ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Analyser
        </button>

        {/* L'import ne se propose qu'après une analyse qui a trouvé quelque
            chose à importer : pas de raccourci vers l'écriture. */}
        {rapport && !rapport.appliquee && (rapport.importables ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => void envoyer(true)}
            disabled={enCours}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importer {rapport.importables} ligne{(rapport.importables ?? 0) > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {erreur && (
        <p className="text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-red-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          {erreur}
        </p>
      )}

      {rapport && (
        <div className="space-y-3">
          <p
            className={`text-sm rounded-lg px-3 py-2 flex items-start gap-2 ${
              rapport.appliquee
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-blue-50 border border-blue-200 text-blue-800'
            }`}
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            {rapport.message}
          </p>

          {(rapport.feuilles ?? []).map((f) => (
            <div key={f.feuille} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex flex-wrap items-baseline gap-x-2">
                {f.feuille}
                <span className="text-xs font-normal text-gray-500">
                  {f.lignes} ligne{f.lignes > 1 ? 's' : ''} lue{f.lignes > 1 ? 's' : ''} —{' '}
                  {rapport.appliquee
                    ? `${f.importes} importée${f.importes > 1 ? 's' : ''}`
                    : `${f.importables} importable${f.importables > 1 ? 's' : ''}`}
                </span>
              </p>

              {f.problemes.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 text-xs">
                  {f.problemes.slice(0, 30).map((p, i) => (
                    <li
                      key={i}
                      className={
                        p.type === 'erreur'
                          ? 'text-red-700 dark:text-red-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }
                    >
                      Ligne {p.ligne} — {p.type} : {p.message}
                    </li>
                  ))}
                  {f.problemes.length > 30 && (
                    <li className="text-gray-400">… et {f.problemes.length - 30} autre(s).</li>
                  )}
                </ul>
              )}

              {f.notes.map((n) => (
                <p key={n} className="mt-1.5 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                  {n}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
